import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLang, translations } from '../i18n';
import { authFetch } from '../utils/auth';
import './MonCompte.css';

const API_USERS_URL = process.env.REACT_APP_API_USERS!;

const MonCompte: React.FC = () => {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const { lang } = useLang();
  const t = translations[lang];

  // État pour le formulaire username
  const [newUsername, setNewUsername] = useState('');
  const [usernameLoading, setUsernameLoading] = useState(false);
  const [usernameError, setUsernameError] = useState('');
  const [usernameSuccess, setUsernameSuccess] = useState('');

  // État pour le formulaire password
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');

  if (!user) {
    navigate('/');
    return null;
  }

  // Validation du mot de passe
  const validatePassword = (password: string): boolean => {
    // Minimum 12 caractères
    if (password.length < 12) return false;
    
    // Contient majuscules
    if (!/[A-Z]/.test(password)) return false;
    
    // Contient minuscules
    if (!/[a-z]/.test(password)) return false;
    
    // Contient chiffres
    if (!/[0-9]/.test(password)) return false;
    
    // Contient symboles
    if (!/[^A-Za-z0-9]/.test(password)) return false;
    
    // Ne contient pas le username (insensible à la casse)
    if (user.username && password.toLowerCase().includes(user.username.toLowerCase())) {
      return false;
    }
    
    return true;
  };

  // Handler pour mise à jour du username
  const handleUpdateUsername = async (e: React.FormEvent) => {
    e.preventDefault();
    setUsernameLoading(true);
    setUsernameError('');
    setUsernameSuccess('');

    try {
      const response = await authFetch(`${API_USERS_URL}/${user.id}/update-username`, {
        method: 'PUT',
        body: JSON.stringify({ username: newUsername })
      });

      if (!response.ok) {
        throw new Error('Erreur lors de la mise à jour');
      }

      // Mettre à jour le user dans le context avec le nouveau username (sans toucher au token)
      updateUser({ ...user, username: newUsername });
      
      setUsernameSuccess(t.updateUsernameSuccess);
      setNewUsername('');
    } catch (error) {
      setUsernameError(t.updateUsernameError);
    } finally {
      setUsernameLoading(false);
    }
  };

  // Handler pour mise à jour du mot de passe
  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordLoading(true);
    setPasswordError('');
    setPasswordSuccess('');

    // Vérifier que les mots de passe correspondent
    if (newPassword !== confirmPassword) {
      setPasswordError(t.passwordMismatch);
      setPasswordLoading(false);
      return;
    }

    // Valider le nouveau mot de passe
    if (!validatePassword(newPassword)) {
      setPasswordError(t.passwordInvalid);
      setPasswordLoading(false);
      return;
    }

    try {
      const response = await authFetch(`${API_USERS_URL}/${user.id}/update-password`, {
        method: 'PUT',
        body: JSON.stringify({ 
          current_password: currentPassword,
          new_password: newPassword 
        })
      });

      if (!response.ok) {
        throw new Error('Erreur lors de la mise à jour');
      }

      setPasswordSuccess(t.updatePasswordSuccess);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      setPasswordError(t.updatePasswordError);
    } finally {
      setPasswordLoading(false);
    }
  };

  // Vérifier en temps réel les règles du mot de passe
  const passwordRulesCheck = {
    length: newPassword.length >= 12,
    uppercase: /[A-Z]/.test(newPassword),
    lowercase: /[a-z]/.test(newPassword),
    number: /[0-9]/.test(newPassword),
    symbol: /[^A-Za-z0-9]/.test(newPassword),
    noUsername: !user.username || !newPassword.toLowerCase().includes(user.username.toLowerCase())
  };

  return (
    <div className="mon-compte-page">
      <div className="mon-compte-header">
        <button className="back-button" onClick={() => navigate('/')}>
          ← {t.backToCatalogue}
        </button>
        <h1>{t.myAccountTitle}</h1>
      </div>

      <div className="mon-compte-content">
        {/* Section Username */}
        <section className="account-section">
          <h2>{t.updateUsernameBtn}</h2>
          <p className="current-info">
            <strong>{t.currentUsername}:</strong> {user.username}
          </p>
          <form onSubmit={handleUpdateUsername}>
            <div className="form-group">
              <input
                type="text"
                placeholder={t.newUsername}
                value={newUsername}
                onChange={(e) => setNewUsername(e.target.value)}
                required
              />
            </div>
            <button type="submit" disabled={usernameLoading || !newUsername}>
              {usernameLoading ? t.updateUsernameLoading : t.updateUsernameBtn}
            </button>
            {usernameSuccess && <div className="success-message">{usernameSuccess}</div>}
            {usernameError && <div className="error-message">{usernameError}</div>}
          </form>
        </section>

        {/* Section Password */}
        <section className="account-section">
          <h2>{t.updatePasswordBtn}</h2>
          <form onSubmit={handleUpdatePassword}>
            <div className="form-group">
              <input
                type="password"
                placeholder={t.currentPassword}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <input
                type="password"
                placeholder={t.newPassword}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <input
                type="password"
                placeholder={t.confirmPassword}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
            </div>

            {/* Règles du mot de passe */}
            {newPassword && (
              <div className="password-rules">
                <p><strong>{t.passwordRules}</strong></p>
                <ul>
                  <li className={passwordRulesCheck.length ? 'valid' : 'invalid'}>
                    {passwordRulesCheck.length ? '✓' : '✗'} {t.passwordRuleLength}
                  </li>
                  <li className={passwordRulesCheck.uppercase ? 'valid' : 'invalid'}>
                    {passwordRulesCheck.uppercase ? '✓' : '✗'} {t.passwordRuleUppercase}
                  </li>
                  <li className={passwordRulesCheck.lowercase ? 'valid' : 'invalid'}>
                    {passwordRulesCheck.lowercase ? '✓' : '✗'} {t.passwordRuleLowercase}
                  </li>
                  <li className={passwordRulesCheck.number ? 'valid' : 'invalid'}>
                    {passwordRulesCheck.number ? '✓' : '✗'} {t.passwordRuleNumber}
                  </li>
                  <li className={passwordRulesCheck.symbol ? 'valid' : 'invalid'}>
                    {passwordRulesCheck.symbol ? '✓' : '✗'} {t.passwordRuleSymbol}
                  </li>
                  <li className={passwordRulesCheck.noUsername ? 'valid' : 'invalid'}>
                    {passwordRulesCheck.noUsername ? '✓' : '✗'} {t.passwordRuleUsername}
                  </li>
                </ul>
              </div>
            )}

            <button type="submit" disabled={passwordLoading || !currentPassword || !newPassword || !confirmPassword}>
              {passwordLoading ? t.updatePasswordLoading : t.updatePasswordBtn}
            </button>
            {passwordSuccess && <div className="success-message">{passwordSuccess}</div>}
            {passwordError && <div className="error-message">{passwordError}</div>}
          </form>
        </section>
      </div>
    </div>
  );
};

export default MonCompte;
